/**
 * MarketPulseDecisionBoard — 2026-06-07 m-canonical-payload.
 *
 * Pulls /api/v1/market-pulse/movers and renders the spec-mandated five
 * sections with definitive decision labels and bias columns:
 *   1. Biggest Gainers       (top_gainers, max 10)
 *   2. Biggest Losers        (top_losers,  max 10)
 *   3. Short Candidates       (losers with strong continuation signal)
 *   4. Reversal Watch         (gainers/losers showing reversal hints)
 *   5. Ignore / Low Quality   (movers with spread/data quality flags)
 *
 * Each row carries:
 *   symbol · price · move% · $ move · volume · catalyst count
 *   bias (Bullish/Bearish/Neutral)
 *   Signal Use — the backend ADV-MKT-BUGFIX-002 safe policy verdict, rendered via
 *     a single enum→display map (WATCHLIST CANDIDATE / SHORT WATCH / REVIEW REQUIRED /
 *     INVESTIGATING / DATA STALE / FEED DIVERGENCE / HIGH RISK / DO NOT ACT / IGNORE /
 *     LOW QUALITY). The table NEVER re-derives an action label from move%; unknown
 *     states fail closed to DO NOT ACT. No action-style buy/sell label is ever emitted.
 *   instrument class — canonical, identical to the heatmap classification.
 *
 * ADV-MKT-PREDEPLOY-001 (2026-06-13): replaced the client-side move%→label synthesis
 * (which reintroduced forbidden action labels) with the backend safe policy.
 *
 * No mock data. If the upstream returns empty buckets, the section card
 * surfaces an honest unavailable empty state.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, AlertOctagon, RefreshCw, Activity, ClipboardList, ListChecks, Info } from 'lucide-react';
import { apiV1Base } from '../lib/apiConfig';
import { coalescedGetJson } from '../lib/requestCoalesce'; // ADV-MKT-PREDEPLOY-003A A1
import { resolveBuyCandidate, BUY_CANDIDATE_MAX_EXTENSION_PCT } from '../lib/buyCandidatePolicy'; // ADV-MKT-SIGNAL-BUYDEF-001
// ADV-MKT-BOARD-CLARITY-ACTIONABILITY-001 — per-row "can I act?" verdict + legend/counts.
import { resolveActionability, summarizeBoard, type Actionability } from '../lib/boardActionability';
import { BoardLegend, BoardSummaryCounts } from './BoardLegend';
// ADV-MKT-WATCHLIST-ADD-ACTION-001 — one-click "+ Watchlist" per board row.
import { WatchlistMembershipProvider, WatchlistAddButton } from './WatchlistAddButton';
import ProviderStackTile from './ProviderStackTile';  // ADV-P0I
import { subscribeRefresh, emitRefresh } from '../lib/refreshEvents';
import MarketsRefreshControl from './MarketsRefreshControl';
import { deriveCatalystDisplay, type CatalystKind } from '../lib/catalystDisplay';
import { formatUsd, formatPercent, formatPnL, EM_DASH } from '../lib/accountFormatters';

interface RawMover {
  symbol?: string;
  price?: number | string;
  change_percent?: number | string;
  change?: number | string;
  volume?: number | string;
  asset_class?: string;
  news_count?: number;
  catalyst_count?: number;
  trend_state?: string | null;
  catalyst_status?: string | null;
  primary_catalyst?: string | null;
  primary_catalyst_display?: string | null;
  primary_catalyst_snippet?: string | null;
  primary_catalyst_source?: string | null;
  primary_catalyst_url?: string | null;
  primary_catalyst_ts?: string | null;
  entity_match_status?: string | null;
  signal_reason?: string | null;
  reason?: string | null;
  // ADV-MKT-BUGFIX-002 safe signal policy (backend-authoritative). The table
  // renders these; it NEVER re-derives an action label from move%.
  signal_use?: string | null;
  signal_use_display?: string | null;
  bias?: string | null;
  instrument_class?: string | null;
  risk_band?: string | null;
  // ADV-MKT-SIGNAL-BUYDEF-001: gating inputs for the fail-closed BUY CANDIDATE ladder.
  // breakout_trigger is the C4 technical trigger (wired onto movers rows; fail-closed when absent).
  breakout_trigger?: number | string | null;
  direction?: string | null;
  calc_mismatch?: boolean | null;
  dq_flags?: string[] | null;
  // G4-UI: backend integrity fields (read-only display).
  integrity_band?: string | null;
  integrity_status?: string | null;
  integrity_decision_band?: string | null;
  integrity_score?: number | null;
  integrity_market_cap?: number | null;
  integrity_market_cap_status?: string | null;
  integrity_shares_confidence?: string | null;
  integrity_fact_coverage?: { critical_present?: number; critical_total?: number; missing_groups?: string[] } | null;
  integrity_missing_facts?: string[] | null;
  integrity_components?: Array<{ check?: string }> | null;
}

interface MoversPayload {
  top_gainers?: RawMover[];
  top_losers?: RawMover[];
  top_tradable_movers?: RawMover[];
  status?: string;
  provider_state?: {
    status?: string;
    display_message?: string;
  };
  // ADV-CLOSED-MARKET: last completed-session snapshot envelope (closed market).
  snapshot_mode?: string | null;
  snapshot_as_of?: string | null;
  market_session_state?: string | null;
  refresh_mode?: string | null;
  retained_catalyst_count?: number | null;
}

// Compact, non-banner session indicator shown only when the backend is serving a
// frozen last-completed-session snapshot (market closed). Text only — no panel.
function _SessionSnapshotChip({ payload }: { payload: MoversPayload }) {
  if (payload?.snapshot_mode !== 'last_completed_session') return null;
  const state = String(payload.market_session_state || 'closed').toLowerCase();
  const asOf = payload.snapshot_as_of ? String(payload.snapshot_as_of) : '';
  let asOfText = '';
  try {
    if (asOf) {
      const d = new Date(asOf);
      if (!Number.isNaN(d.getTime())) {
        asOfText = d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      }
    }
  } catch { /* ignore */ }
  return (
    <span
      data-testid="mp-session-snapshot"
      data-session-state={state}
      data-snapshot-mode="last_completed_session"
      title={`Market ${state}. Showing the last completed session snapshot; refresh is overlay-only (verified catalyst updates only). As of ${asOfText || asOf}.`}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-widest bg-slate-200 text-slate-600 border border-slate-400/40"
    >
      Last session{asOfText ? ` · as of ${asOfText}` : ''} · overlay only
    </span>
  );
}

type Bias = 'Bullish' | 'Bearish' | 'Neutral';
type Trend = 'Continuing' | 'Reversing' | 'Unknown';
type BucketKind = 'short' | 'review' | 'ignore' | 'watch';

interface DecisionRow {
  symbol: string;
  price: number | null;
  movePct: number | null;
  dollarMove: number | null;
  volume: number | null;
  instrumentKey: string;
  instrumentLabel: string;
  catalystCount: number;
  bias: Bias;
  signalLabel: string;
  signalTone: string;
  bucket: BucketKind;
  trend: Trend;
  reason: string;
  catalystText: string;
  catalystTooltip: string;
  catalystFull: string;
  catalystKind: CatalystKind;
  integrityBand: string | null;
  integrityDecisionBand: string | null;
  integrityStatus: string | null;
  integrityScore: number | null;
  integrityMarketCap: number | null;
  integritySharesConf: string | null;
  integrityCovPresent: number | null;
  integrityCovTotal: number | null;
  integrityMissing: string[];
  integrityComponents: string[];
  action: Actionability; // ADV-MKT-BOARD-CLARITY-ACTIONABILITY-001
  raw: RawMover;
}

function _asNum(v: any): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

// ── ADV-MKT-PREDEPLOY-001 — SINGLE source of Signal Use label text ──────────
// The backend ADV-MKT-BUGFIX-002 policy is authoritative. This map is the ONLY
// place the Markets table turns a backend signal_use enum into a human label,
// chip tone, and section bucket. The table NEVER re-derives an action label from
// move%. Unknown / unmapped signal_use fails CLOSED to DO NOT ACT. No code path
// ADV-P0N: BUY CANDIDATE / SHORT CANDIDATE are restored as setup CATEGORY labels for
// genuine buy_candidate/short_candidate rows. They are NOT order instructions: the
// board never enables an order control, degraded rows carry different keys (DATA STALE/
// DO NOT ACT/...), and the standing disclaimer affirms Adversyn never places orders.
const _SLATE = 'bg-slate-300 text-slate-700';
const _ROSE = 'bg-rose-600 text-white';
const _ORANGE = 'bg-orange-600 text-white';
const _AMBER = 'bg-amber-500 text-white';
const _EMERALD = 'bg-emerald-600 text-white';
const _TEAL = 'bg-teal-600 text-white'; // ADV-MKT-SIGNAL-BUYDEF-001 APPROACHING BREAKOUT (distinct state)
const _EMERALD_SOFT = 'bg-emerald-500/70 text-white'; // ADV-MKT-SIGNAL-BUYDEF-001 WATCHLIST CANDIDATE (gated)

interface SignalSpec { label: string; tone: string; bucket: BucketKind; }
const SIGNAL_POLICY: Record<string, SignalSpec> = {
  watchlist_candidate:        { label: 'BUY CANDIDATE',   tone: _EMERALD, bucket: 'watch' },   // ADV-P0N category (watch-only; see disclaimer)
  short_candidate:            { label: 'SHORT CANDIDATE', tone: _ROSE,    bucket: 'short' },   // ADV-P0N
  short_watch:                { label: 'SHORT CANDIDATE', tone: _ROSE,    bucket: 'short' },
  reversal_watch:             { label: 'REVERSAL WATCH',      tone: _AMBER,   bucket: 'review' },
  needs_catalyst_review:      { label: 'REVIEW REQUIRED',     tone: _AMBER,   bucket: 'review' },
  catalyst_search_required:   { label: 'REVIEW REQUIRED',     tone: _AMBER,   bucket: 'review' },
  entity_review:              { label: 'REVIEW REQUIRED',     tone: _AMBER,   bucket: 'review' },
  investigating_catalyst:     { label: 'INVESTIGATING',       tone: _SLATE,   bucket: 'review' },
  last_verified_catalyst:     { label: 'REVIEW REQUIRED',     tone: _AMBER,   bucket: 'review' },
  no_verified_catalyst_found: { label: 'REVIEW REQUIRED',     tone: _SLATE,   bucket: 'review' },
  leveraged_long:             { label: 'REVIEW REQUIRED',     tone: _SLATE,   bucket: 'review' },
  leveraged_unverified:       { label: 'REVIEW REQUIRED',     tone: _SLATE,   bucket: 'review' },
  bias_suppressed:            { label: 'DO NOT ACT',          tone: _SLATE,   bucket: 'ignore' },
  high_risk:                  { label: 'HIGH RISK',           tone: _ORANGE,  bucket: 'ignore' },
  avoid:                      { label: 'DO NOT ACT',          tone: _ROSE,    bucket: 'ignore' },
  do_not_act:                 { label: 'DO NOT ACT',          tone: _ROSE,    bucket: 'ignore' },
  underlying_conflict:        { label: 'DO NOT ACT',          tone: _ROSE,    bucket: 'ignore' },
  calc_mismatch:              { label: 'DO NOT ACT',          tone: _ROSE,    bucket: 'ignore' },
  data_stale:                 { label: 'DATA STALE',          tone: _SLATE,   bucket: 'ignore' },
  feed_divergence:            { label: 'FEED DIVERGENCE',     tone: _SLATE,   bucket: 'ignore' },
  data_limited:               { label: 'LOW QUALITY',         tone: _SLATE,   bucket: 'ignore' },
  ignore:                     { label: 'IGNORE',              tone: _SLATE,   bucket: 'ignore' },
};
const _FAIL_CLOSED: SignalSpec = { label: 'DO NOT ACT', tone: _ROSE, bucket: 'ignore' };
// Hard allow-list (ADV-MKT-BUGFIX-002). Anything outside it is forced to DO NOT ACT.
const _ALLOWED_LABELS = new Set<string>([
  'BUY CANDIDATE', 'SHORT CANDIDATE',  // ADV-P0N restored categories (watch-only)
  'APPROACHING BREAKOUT', // ADV-MKT-SIGNAL-BUYDEF-001 distinct gated state
  'WATCHLIST CANDIDATE', 'SHORT WATCH', 'REVERSAL WATCH', 'IGNORE', 'DO NOT ACT',
  'REVIEW REQUIRED', 'LOW QUALITY', 'DATA STALE', 'FEED DIVERGENCE', 'HIGH RISK', 'INVESTIGATING',
]);

interface ResolvedSignal extends SignalSpec { reason?: string; }
const _LADDER_TONE: Record<string, string> = {
  'DO NOT ACT': _ROSE, 'HIGH RISK': _ORANGE, 'REVIEW REQUIRED': _AMBER,
  'BUY CANDIDATE': _EMERALD, 'APPROACHING BREAKOUT': _TEAL, 'WATCHLIST CANDIDATE': _EMERALD_SOFT,
};
const _LADDER_BUCKET: Record<string, BucketKind> = {
  'DO NOT ACT': 'ignore', 'HIGH RISK': 'ignore', 'REVIEW REQUIRED': 'review',
  'BUY CANDIDATE': 'watch', 'APPROACHING BREAKOUT': 'watch', 'WATCHLIST CANDIDATE': 'watch',
};
function _unsafeShortReason(r: RawMover): string {
  const dq = Array.isArray(r.dq_flags) ? r.dq_flags.map((x) => String(x).toUpperCase()) : [];
  const status = String(r.integrity_status || '').toUpperCase();
  const band = String(r.integrity_band || '').toUpperCase();
  // ADV-MKT-BUY-CANDIDATE-CONTRACT-001 — canonical decision band with fallback (mirrors
  // backend red_flag_scorer.decision_band): use integrity_decision_band when present,
  // else derive CLEAR iff raw band CLEAR AND facts SCORED. Keeps SHORT-candidate gating
  // working on the current live payload (which omits integrity_decision_band).
  const decisionBand = String(r.integrity_decision_band || '').toUpperCase()
    || (band === 'CLEAR' && status === 'SCORED' ? 'CLEAR' : 'REVIEW_REQUIRED');
  const cat = String(r.catalyst_status || '').toUpperCase();
  const ic = String(r.instrument_class || '').toLowerCase().trim();
  // ADV-MKT-BUY-CANDIDATE-CONTRACT-001 — align staleness with boardActionability._isStale
  // (also blocks "stale_market_closed" quote_freshness, not just dq flags).
  const qf = String((r as any).quote_freshness || (r as any).data_quality || '').toLowerCase();
  if (dq.includes('STALE') || dq.includes('QUOTE_STALE') || qf.includes('stale')) return 'stale quote';
  if (dq.includes('FEED_DIVERGENCE')) return 'feed divergence';
  if (dq.includes('CALC_MISMATCH') || r.calc_mismatch === true) return 'calculation mismatch';
  if (ic === 'leveraged_inverse') return 'inverse leveraged instrument';
  if (decisionBand !== 'CLEAR') {
    if (status === 'INSUFFICIENT_FACTS') return 'integrity INSUFFICIENT';
    if (status === 'PARTIAL_FACTS') return 'integrity PARTIAL';
    return decisionBand ? 'decision band ' + decisionBand.toLowerCase().replace(/_/g, ' ') : 'integrity decision band missing';
  }
  if (cat !== 'CATALYST_VERIFIED' && cat !== 'VERIFIED') return 'missing catalyst verification';
  return '';
}

function _resolveSignal(r: RawMover): ResolvedSignal {
  const key = String(r.signal_use || '').toLowerCase().trim();
  const spec = SIGNAL_POLICY[key] || _FAIL_CLOSED;
  // Defense in depth: never render a label outside the allow-list, regardless of input.
  const safe = _ALLOWED_LABELS.has(spec.label) ? spec : _FAIL_CLOSED;
  // ADV-MKT-SIGNAL-BUYDEF-001: BUY CANDIDATE is a fail-closed COMPUTED state. Re-gate any
  // backend buy-candidate row through the ladder so it can NEVER render BUY CANDIDATE while
  // price < breakout_trigger (or when integrity/catalyst/dq/instrument gates fail).
  if (safe.label === 'BUY CANDIDATE') {
    const lad = resolveBuyCandidate(r, { maxExtensionPct: BUY_CANDIDATE_MAX_EXTENSION_PCT });
    const label = _ALLOWED_LABELS.has(lad.label) ? lad.label : 'DO NOT ACT';
    return { label, tone: _LADDER_TONE[label] || _FAIL_CLOSED.tone, bucket: _LADDER_BUCKET[label] || 'review', reason: lad.reason };
  }
  if (safe.label === 'SHORT CANDIDATE') {
    const reason = _unsafeShortReason(r);
    if (reason) return { label: 'REVIEW REQUIRED', tone: _AMBER, bucket: 'review', reason };
  }
  return safe;
}

// Canonical instrument classification — IDENTICAL precedence to the heatmap
// (_heatLane in MarketsModule): backend instrument_class first (leveraged), then
// asset_class. Guarantees the table badge and heatmap bucket agree for every symbol.
function _instrument(r: RawMover): { key: string; label: string } {
  const ic = String(r.instrument_class || '').toLowerCase();
  if (ic === 'leveraged_long') return { key: 'leveraged_long', label: 'LEVERAGED LONG' };
  if (ic === 'leveraged_inverse') return { key: 'leveraged_inverse', label: 'LEVERAGED INVERSE' };
  if (ic === 'leveraged_unverified' || ic === 'leveraged_uncertain') return { key: 'leveraged_unverified', label: 'LEVERAGED — VERIFY' };
  const ac = String(r.asset_class || '').toLowerCase();
  if (ac === 'crypto') return { key: 'crypto', label: 'CRYPTO' };
  if (ac === 'etf') return { key: 'etf', label: 'ETF' };
  if (ac === 'us_equity' || ac === 'equity' || ac === 'stock') return { key: 'equity', label: 'EQUITY' };
  if (ac === 'forex' || ac === 'fx' || ac === 'currency') return { key: 'forex', label: 'FOREX' };
  return { key: 'unknown', label: 'UNKNOWN' };
}

function _biasFrom(r: RawMover, movePct: number | null): Bias {
  const b = String(r.bias || '').toLowerCase();
  if (b === 'bullish') return 'Bullish';
  if (b === 'bearish') return 'Bearish';
  if (b === 'neutral' || b === 'suppressed') return 'Neutral';
  // observational fallback only when backend bias is absent
  if (movePct !== null) return movePct > 0 ? 'Bullish' : movePct < 0 ? 'Bearish' : 'Neutral';
  return 'Neutral';
}

// ADV-MKT-PREDEPLOY-002: TREND was hard-coded 'Unknown'. The backend already emits
// a per-row `trend_state` (continuing/reversing/unknown). Render it through the
// project's canonical Trend enum; 'Unknown' ONLY when the backend value is absent/unknown.
function _trendFrom(r: RawMover): Trend {
  const t = String(r.trend_state || '').trim().toLowerCase();
  if (t === 'continuing' || t === 'continuation' || t === 'trending') return 'Continuing';
  if (t === 'reversing' || t === 'reversal') return 'Reversing';
  return 'Unknown';
}

function _classifyRow(r: RawMover): DecisionRow {
  const symbol = String(r.symbol || '').toUpperCase();
  const price = _asNum(r.price);
  const movePct = _asNum(r.change_percent);
  const dollarMove = _asNum(r.change);
  const volume = _asNum(r.volume);
  const catalystCount = Number(r.catalyst_count || r.news_count || 0);
  const instrument = _instrument(r);
  const sig = _resolveSignal(r);
  const bias = _biasFrom(r, movePct);
  // Reason is the backend's safe policy text — never a UI-synthesized label string.
  // ADV-MKT-SIGNAL-BUYDEF-001: when the fail-closed ladder demoted a buy-candidate row,
  // surface WHY it is not a buy (e.g. "below breakout $1.80", "integrity PARTIAL") first.
  const _ladderReason = (sig as ResolvedSignal).reason ? String((sig as ResolvedSignal).reason) : '';
  const _backendReason = String(r.signal_reason || r.reason || 'Pending catalyst verification.');
  const reason = _ladderReason ? (_ladderReason + ' — ' + _backendReason) : _backendReason;

  const _cat = deriveCatalystDisplay(r as any, { fallbackReason: reason });
  // ADV-P0: full, deduped catalyst/reason text for the hover detail (headline + source/
  // status tooltip + signal reason). Lets the truncated cell expose everything on hover.
  const _catalystFull = Array.from(new Set(
    [_cat.text, _cat.tooltip, reason].map((x) => String(x || '').trim()).filter(Boolean),
  )).join(' — ');
  const _cov: any = r.integrity_fact_coverage || {};
  const integrityMissing = Array.isArray(_cov.missing_groups)
    ? _cov.missing_groups.map((x: any) => String(x))
    : (Array.isArray(r.integrity_missing_facts) ? r.integrity_missing_facts.map((x: any) => String(x)) : []);
  const integrityComponents = Array.isArray(r.integrity_components)
    ? r.integrity_components.map((c: any) => String(c?.check || '')).filter(Boolean)
    : [];
  return {
    raw: r,
    symbol,
    price,
    movePct,
    dollarMove,
    volume,
    instrumentKey: instrument.key,
    instrumentLabel: instrument.label,
    catalystCount,
    bias,
    signalLabel: sig.label,
    signalTone: sig.tone,
    bucket: sig.bucket,
    trend: _trendFrom(r),
    reason,
    catalystText: _cat.text,
    catalystTooltip: _cat.tooltip,
    catalystFull: _catalystFull,
    catalystKind: _cat.kind,
    integrityBand: r.integrity_band ?? null,
    integrityDecisionBand: r.integrity_decision_band ?? null,
    integrityStatus: r.integrity_status ?? null,
    integrityScore: _asNum(r.integrity_score),
    integrityMarketCap: _asNum(r.integrity_market_cap),
    integritySharesConf: r.integrity_shares_confidence ?? null,
    integrityCovPresent: _asNum(_cov.critical_present),
    integrityCovTotal: _asNum(_cov.critical_total),
    integrityMissing,
    integrityComponents,
    action: resolveActionability(r as any),
  };
}

function _fmtCap(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return '';
  const a = Math.abs(v);
  if (a >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (a >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

// ADV-MKT-CAT-COLUMN-LAYOUT-001 — compact share-volume label (no currency) so the Volume
// column stays narrow and never bleeds; full count preserved in the cell title tooltip.
function _fmtVol(v: number | null): string {
  if (v === null || !Number.isFinite(v)) return EM_DASH;
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return String(Math.round(v));
}

function _missingIntegrityReason(row: DecisionRow): string {
  const vals = [...row.integrityMissing, ...row.integrityComponents]
    .map((x) => String(x || '').toLowerCase().trim())
    .filter(Boolean);
  const has = (rx: RegExp) => vals.some((x) => rx.test(x));
  if (has(/market[_ -]?cap|valuation/)) return 'missing market cap';
  if (has(/float|shares[_ -]?outstanding/)) return 'missing float';
  if (has(/rvol|relative[_ -]?volume|volume/)) return 'missing rvol';
  if (has(/quote|fresh|liquidity|spread/)) return 'missing quote freshness';
  if (has(/bars|history/)) return 'missing bars';
  if (has(/issuer|cik/)) return 'missing issuer CIK';
  if (has(/catalyst/)) return 'missing catalyst verification';
  return '';
}

// ADV-MKT-BOARD-LAYOUT-POLISH-001 — short form of the missing-fact reason for the
// compact integrity chip suffix; abbreviated so the chip stays on ONE line inside the
// narrow Integrity column (e.g. "mkt cap" not "missing market cap"). Full detail is
// always available in the chip's title tooltip.
function _missingShort(row: DecisionRow): string {
  const r = _missingIntegrityReason(row).replace(/^missing\s+/, '');
  const map: Record<string, string> = {
    'market cap': 'mkt cap',
    'quote freshness': 'quote',
    'catalyst verification': 'catalyst',
    'issuer CIK': 'CIK',
  };
  return map[r] || r;
}

function _catalystCountDisplay(n: number): string {
  return Number.isFinite(n) && n >= 5 ? '5+' : String(n || 0);
}

// G4-UI: compact row-level Integrity chip. Read-only display of the backend
// integrity band/status. INSUFFICIENT never renders green; CLEAR only renders
// when the backend reports CLEAR with a SCORED status. No banners, no modals.
function _IntegrityChip({ row }: { row: DecisionRow }) {
  const band = (row.integrityBand || '').toUpperCase();
  const status = (row.integrityStatus || '').toUpperCase();
  if (!band && !status) {
    return (
      <span data-testid="mp-integrity-unavailable" title="Integrity unavailable"
            className="inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest bg-slate-200 text-slate-500">
        &mdash;
      </span>
    );
  }
  let label = '\u2014';
  // ADV-MKT-BOARD-LAYOUT-POLISH-001 — PARTIAL / INSUFF rendered as a compact, muted
  // two-part chip ("PARTIAL · market cap") instead of a large loud white chip. Status
  // is never hidden; the full missing-fact detail stays in the title tooltip.
  let subLabel = '';
  let compact = false;
  let key = 'other';
  let tone = 'bg-slate-200 text-slate-500';
  if (status === 'INSUFFICIENT_FACTS') {
    label = 'INSUFF'; subLabel = _missingShort(row); compact = true; key = 'insuff';
    tone = 'bg-slate-500/10 text-[var(--muted-text)] border border-slate-400/40';
  } else if (status === 'PARTIAL_FACTS') {
    label = 'PARTIAL'; subLabel = _missingShort(row); compact = true; key = 'partial';
    tone = 'bg-slate-500/10 text-[var(--muted-text)] border border-dashed border-slate-400/50';
  } else if (band === 'DO_NOT_TRADE') {
    label = 'DO NOT TRADE'; key = 'do-not-trade'; tone = 'bg-rose-700 text-white border border-rose-900';
  } else if (band === 'HIGH_RISK') {
    label = 'HIGH RISK'; key = 'high-risk'; tone = 'bg-orange-600 text-white border border-orange-800';
  } else if (band === 'CAUTION') {
    label = 'CAUTION'; key = 'caution'; tone = 'bg-amber-500 text-white border border-amber-700';
  } else if (band === 'CLEAR') {
    label = 'CLEAR'; key = 'clear'; tone = 'bg-emerald-600 text-white border border-emerald-800';
  }
  const parts: string[] = [];
  if (row.integrityScore !== null) parts.push(`Score ${row.integrityScore}`);
  const cap = _fmtCap(row.integrityMarketCap);
  if (cap) parts.push(`Mkt cap ${cap}`);
  if (row.integritySharesConf) parts.push(`Shares ${row.integritySharesConf}`);
  if (row.integrityCovPresent !== null && row.integrityCovTotal !== null) {
    parts.push(`Coverage ${row.integrityCovPresent}/${row.integrityCovTotal}`);
  }
  if (row.integrityComponents.length) parts.push(`Flags: ${row.integrityComponents.join(', ')}`);
  if (row.integrityMissing.length) parts.push(`Missing: ${row.integrityMissing.join(', ')}`);
  const title = parts.length ? parts.join(' \u00b7 ') : 'Integrity advisory';
  return (
    <span data-testid={`mp-integrity-${key}`} data-integrity-band={band || ''} data-integrity-status={status || ''}
          title={title}
          className={compact
            ? `inline-flex flex-wrap items-center gap-x-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${tone}`
            : `inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wide ${tone}`}>
      <span className="font-black">{label}</span>
      {compact && subLabel ? <span className="font-normal normal-case opacity-80">· {subLabel}</span> : null}
    </span>
  );
}

function _BiasChip({ bias }: { bias: Bias }) {
  const tone =
    bias === 'Bullish' ? 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' :
    bias === 'Bearish' ? 'bg-rose-500/15 text-rose-700 border-rose-500/30' :
    'bg-slate-500/15 text-slate-600 border-slate-500/30';
  return (
    <span data-testid={`mp-bias-${bias.toLowerCase()}`}
          title={`Bias: ${bias}. Directional lean from the verified catalyst / move; informational, NOT an action instruction. A bullish bias alone does not make a row actionable.`}
          className={`inline-block px-1.5 py-0.5 border rounded-full text-[10px] font-black uppercase tracking-wide ${tone}`}>
      {bias}
    </span>
  );
}

// ADV-MKT-BOARD-CLARITY-ACTIONABILITY-001 — Signal Use chip tooltips.
const _SIGNAL_TOOLTIP: Record<string, string> = {
  'BUY CANDIDATE': 'BUY CANDIDATE: actionable setup candidate — fires only when integrity is CLEAR, catalyst verified, bias bullish, data fresh, and price is in the breakout entry window. Watch-only; Adversyn never places orders.',
  'WATCHLIST CANDIDATE': 'WATCHLIST CANDIDATE: promising but not actionable yet (e.g. price extended beyond the entry window or below the trigger).',
  'APPROACHING BREAKOUT': 'APPROACHING BREAKOUT: clean setup but price is still below the breakout trigger — not in the entry window yet.',
  'SHORT CANDIDATE': 'SHORT CANDIDATE: bearish setup category (watch-only). Requires confirmed bearish evidence; never an order instruction.',
  'REVIEW REQUIRED': 'REVIEW REQUIRED: human review required; not actionable. Hover the Why? icon for the specific blocker.',
  'REVERSAL WATCH': 'REVERSAL WATCH: possible reversal; monitor only, not actionable.',
  'INVESTIGATING': 'INVESTIGATING: catalyst investigation in progress; not actionable.',
  'DATA STALE': 'DATA STALE: quote/catalyst/integrity freshness failed. Not actionable until refreshed.',
  'FEED DIVERGENCE': 'FEED DIVERGENCE: cached move disagrees with the live quote. Not actionable until reconciled.',
  'HIGH RISK': 'HIGH RISK: red flags present. Not actionable.',
  'DO NOT ACT': 'DO NOT ACT: blocked by safety / data-quality / instrument gates.',
  'LOW QUALITY': 'LOW QUALITY: insufficient data quality to act.',
  'IGNORE': 'IGNORE: not a tradable setup this window.',
};
function _SignalChip({ label, tone }: { label: string; tone: string }) {
  const tip = _SIGNAL_TOOLTIP[label] || `${label}: advisory signal-use label. Hover the Why? icon for actionability detail.`;
  return (
    <span data-testid={`mp-signal-${label.replace(/\s+/g, '-').toLowerCase()}`}
          data-signal-label={label}
          title={tip}
          className={`inline-block px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wide leading-tight ${tone}`}>
      {label}
    </span>
  );
}

// ADV-MKT-CAT-COLUMN-LAYOUT-001 — compact Class labels so long leveraged labels no longer
// bleed into the CAT column. Presentation only (underlying instrument value unchanged);
// the full label is preserved in the title tooltip + data-instrument-class.
const _CLASS_SHORT: Record<string, string> = {
  leveraged_long: 'LEV LONG',
  leveraged_inverse: 'LEV INV',
  leveraged_unverified: 'LEV VFY',
};
function _InstrumentBadge({ row }: { row: DecisionRow }) {
  const short = _CLASS_SHORT[row.instrumentKey] || row.instrumentLabel;
  return (
    <span data-testid="mp-instrument-badge" data-instrument-class={row.instrumentKey}
          title={row.instrumentLabel}
          className="block truncate text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-wide">
      {short}
    </span>
  );
}

function _WhyPopover({ row }: { row: DecisionRow }) {
  // ADV-MKT-BOARD-CLARITY-ACTIONABILITY-001 — compact row-level "Why?" explanation.
  // Hover/focus popover (no banner). Mirrors the catalyst-cell pattern.
  const a = row.action;
  const lines: Array<[string, string]> = [
    ['Action status', a.actionStatus],
    ['Primary blocker', a.primaryBlocker],
    ['Missing facts', a.missingFacts.length ? a.missingFacts.join(', ') : 'none'],
    ['Freshness', a.freshness],
    ['Catalyst', a.catalyst],
    ['Breakout', a.breakout],
  ];
  const titleText = lines.map(([k, v]) => `${k}: ${v}`).join('\n');
  return (
    <span className="group relative inline-flex shrink-0">
      <button
        type="button"
        data-testid="mp-why-toggle"
        data-symbol={row.symbol}
        data-action-category={a.category}
        title={titleText}
        aria-label={`Why ${row.symbol} is ${a.actionStatus}`}
        className="inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--card-border)] text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--input-bg)] focus:outline-none focus:text-[var(--app-text)]"
      >
        <Info size={11} />
      </button>
      <span
        data-testid="mp-why-detail"
        data-symbol={row.symbol}
        role="tooltip"
        className="pointer-events-none absolute left-0 top-full z-30 mt-1 hidden w-64 max-w-[80vw] whitespace-normal break-words rounded-md border border-[var(--card-border)] bg-[var(--card-bg)] px-2 py-1.5 text-[11px] font-normal normal-case tracking-normal leading-snug text-[var(--app-text)] shadow-lg group-hover:block group-focus-within:block"
      >
        {lines.map(([k, v]) => (
          <span key={k} className="block"><span className="font-bold">{k}:</span> {v}</span>
        ))}
      </span>
    </span>
  );
}

function _Row({ row }: { row: DecisionRow }) {
  // ADV-MKT-BOARD-LAYOUT-POLISH-001 — visual hierarchy only (NO scoring/logic change):
  //  • stale/partial rows stay muted (unchanged)
  //  • leveraged REVIEW rows are de-emphasised so they don't bury equity candidates
  //  • candidate rows get a left accent + faint tint so they are easy to spot
  const cat = row.action.category;
  const isStaleOrPartial = cat === 'Stale' || cat === 'Partial';
  const isLeveraged = row.instrumentKey.startsWith('leveraged');
  const isCandidate = row.action.isActionable
    || ['BUY CANDIDATE', 'WATCHLIST CANDIDATE', 'SHORT CANDIDATE', 'APPROACHING BREAKOUT'].includes(row.signalLabel);
  const bearish = row.bias === 'Bearish';
  let accent = 'border-l-2 border-l-transparent';
  if (isCandidate && !isStaleOrPartial) {
    accent = bearish
      ? 'border-l-2 border-l-rose-500/70 bg-rose-500/[0.06]'
      : 'border-l-2 border-l-emerald-500/70 bg-emerald-500/[0.06]';
  }
  let dim = '';
  if (isStaleOrPartial) dim = ' opacity-60 italic';
  else if (isLeveraged && !isCandidate) dim = ' opacity-70';
  const emphasis = (isCandidate && !isStaleOrPartial) ? (bearish ? 'short-candidate' : 'candidate') : (isLeveraged ? 'leveraged' : 'normal');
  return (
    <tr data-testid="mp-decision-row" data-symbol={row.symbol}
        data-action-category={row.action.category}
        data-actionable={row.action.isActionable ? 'true' : 'false'}
        data-row-emphasis={emphasis}
        className={`border-b border-[var(--card-border)] ${accent} hover:bg-[var(--input-bg)] transition-colors${dim}`}>
      {/* ADV-MKT-SYMBOL-ICON-OVERLAP-001 — stable symbol cell: icons never shrink,
          ticker truncates inside the cell, so nothing spills into the Price column. */}
      <td data-testid="mp-symbol-cell" className="px-3 py-2 text-sm font-black text-[var(--app-text)] italic">
        <div className="flex items-center gap-1.5 min-w-0">
          {/* ADV-UIACCESS: the ticker itself opens the stock-detail chart view
              (adversyn:symbol-selected). The note icon still opens the plan drawer. */}
          <_WhyPopover row={row} />
          <button
            type="button"
            data-testid="mp-symbol-open"
            data-symbol={row.symbol}
            title={`Open ${row.symbol} chart`}
            aria-label={`Open ${row.symbol} chart`}
            onClick={() => { try { window.dispatchEvent(new CustomEvent('adversyn:symbol-selected', { detail: { value: row.symbol } })); } catch { /* ignore */ } }}
            className="min-w-0 truncate font-black italic text-[var(--app-text)] underline-offset-2 hover:underline hover:text-emerald-600 focus:outline-none focus:underline cursor-pointer"
          >
            {row.symbol}
          </button>
          <button
            type="button"
            data-testid="mp-plan-action"
            data-symbol={row.symbol}
            title="Plan paper session"
            aria-label={`Plan paper session for ${row.symbol}`}
            onClick={() => { try { window.dispatchEvent(new CustomEvent('adversyn:plan-paper-session', { detail: row.raw || { symbol: row.symbol } })); } catch { /* ignore */ } }}
            className="shrink-0 inline-flex items-center justify-center h-5 w-5 rounded border border-[var(--card-border)] text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--input-bg)]"
          >
            <ClipboardList size={11} />
          </button>
        </div>
      </td>
      {/* ADV-MKT-CAT-COLUMN-LAYOUT-001 — numeric cells: px-2 + truncate + tabular-nums so a
          large value clips (ellipsis) instead of bleeding into the next column. Volume is
          abbreviated (6.2M / 1.2B) with the full count in the title tooltip. */}
      <td data-testid="mp-price-cell" className="px-2 py-2 text-[13px] font-bold text-[var(--app-text)] truncate tabular-nums" title={row.price === null ? undefined : formatUsd(row.price)}>{row.price === null ? EM_DASH : formatUsd(row.price)}</td>
      <td className="px-2 py-2 text-[13px] font-bold truncate tabular-nums">{row.movePct === null ? EM_DASH : formatPercent(row.movePct)}</td>
      <td className="px-2 py-2 text-[13px] font-bold truncate tabular-nums hidden md:table-cell">{row.dollarMove === null ? EM_DASH : formatPnL(row.dollarMove)}</td>
      <td className="px-2 py-2 text-[11px] font-bold tabular-nums truncate hidden lg:table-cell text-[var(--muted-text)]" title={row.volume === null ? undefined : row.volume.toLocaleString()}>{row.volume === null ? EM_DASH : _fmtVol(row.volume)}</td>
      {/* ADV-MKT-CAT-COLUMN-LAYOUT-001 — Class cell now matches its header's
          `hidden lg:table-cell` (was always-visible -> 1-column misalignment below lg);
          tighter px-2 + truncated compact label so it cannot bleed into CAT. */}
      <td className="px-2 py-2 hidden lg:table-cell"><_InstrumentBadge row={row} /></td>
      <td data-testid="mp-cat-cell" className="px-2 py-2 text-xs font-bold text-[var(--app-text)] text-center whitespace-nowrap hidden xl:table-cell"
          title={`Catalyst count: ${row.catalystCount} sourced item(s) linked to this symbol. Count alone is not verification — see the Signal Use chip and Why? for whether a catalyst is verified.`}>{_catalystCountDisplay(row.catalystCount)}</td>
      {/* ADV-MKT-CAT-COLUMN-LAYOUT-001 — chip cells clip (overflow-hidden) as a hard safety
          net so a chip can never bleed into the neighbouring column; chips are sized to fit
          so clipping does not trigger for real labels. */}
      <td className="px-2 py-2 overflow-hidden"><_BiasChip bias={row.bias} /></td>
      <td className="px-2 py-2 overflow-hidden"><_SignalChip label={row.signalLabel} tone={row.signalTone} /></td>
      <td className="px-2 py-2 overflow-hidden"><_IntegrityChip row={row} /></td>
      {/* ADV-MKT-CAT-COLUMN-LAYOUT-001 — compact trend label so it fits the narrow Trend
          column (Continuing->CONT, Reversing->REV, Unknown->—); full text in the title. */}
      <td className="px-2 py-2 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-wide hidden xl:table-cell truncate"
          title={`Trend: ${row.trend}. Continuing = move extends the prior trend; Reversing = counter-trend; Unknown = backend has no trend read.`}>{row.trend === 'Continuing' ? 'CONT' : row.trend === 'Reversing' ? 'REV' : EM_DASH}</td>
      <td className="px-3 py-2 text-xs">
        {/* ADV-MKT-BOARD-LAYOUT-POLISH-001 — short truncated reason inline + a small
            info button that opens a COMPACT, bounded, scrollable popover. The popover
            is gated on the icon (hover/focus), so moving the mouse across the table no
            longer pops a giant tooltip over neighbouring rows. Native title kept as a
            fallback; full text stays in the DOM for inspection. */}
        <div className="flex items-start gap-1">
          <span
            data-testid="mp-catalyst-cell"
            data-symbol={row.symbol}
            data-catalyst-kind={row.catalystKind}
            data-catalyst-full={row.catalystFull}
            title={row.catalystFull}
            className={`block truncate min-w-0 ${row.catalystKind === 'verified' ? 'text-[var(--app-text)] font-semibold' : row.catalystKind === 'review' ? 'text-amber-600' : 'text-[var(--muted-text)]'}`}
          >
            {row.catalystText}
          </span>
          <span className="group relative inline-flex shrink-0">
            <button
              type="button"
              data-testid="mp-catalyst-more"
              data-symbol={row.symbol}
              aria-label={`Show full catalyst / reason for ${row.symbol}`}
              title="Show full catalyst / reason"
              className="inline-flex items-center justify-center h-4 w-4 rounded border border-[var(--card-border)] text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--input-bg)] focus:outline-none focus:text-[var(--app-text)]"
            >
              <Info size={10} />
            </button>
            <span
              data-testid="mp-catalyst-detail"
              data-symbol={row.symbol}
              role="tooltip"
              className="absolute right-0 top-full z-40 mt-1 hidden w-[360px] max-w-[80vw] max-h-[200px] overflow-y-auto whitespace-normal break-words rounded-md border border-[var(--card-border)] bg-[var(--card-bg)] px-3 py-2 text-[11px] font-normal normal-case tracking-normal leading-snug text-[var(--app-text)] shadow-lg group-hover:block group-focus-within:block"
            >
              {row.catalystFull}
            </span>
          </span>
        </div>
      </td>
      {/* ADV-MKT-WATCHLIST-ADD-ACTION-001 — dedicated Actions cell (watch only). */}
      <td className="px-3 py-2 whitespace-nowrap">
        <WatchlistAddButton symbol={row.symbol} />
      </td>
    </tr>
  );
}

function _Section({ testid, title, icon: Icon, rows, emptyLabel, onRefresh, refreshing, lastUpdatedAt }: {
  testid: string;
  title: string;
  icon: any;
  rows: DecisionRow[];
  emptyLabel: string;
  onRefresh: () => void;
  refreshing: boolean;
  lastUpdatedAt: number | null;
}) {
  // 2026-06-07 r-batch: top 5 by default + show-all/collapse toggle;
  // table sized for full desktop width without horizontal scroll.
  const [showAll, setShowAll] = useState(false);
  const TOP_N = 5;
  const display = showAll ? rows : rows.slice(0, TOP_N);
  const canExpand = rows.length > TOP_N;
  return (
    <div data-testid={testid} className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm w-full">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-[var(--muted-text)]" />
          <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic tracking-tight">{title}</h3>
          <span data-testid={testid + '-count'} className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">({rows.length})</span>
          {!showAll && rows.length > 0 && (
            <span data-testid="mp-section-top-five" className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">
              Top {Math.min(TOP_N, rows.length)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
        {canExpand && (
          showAll ? (
            <button
              data-testid="mp-section-collapse"
              onClick={() => setShowAll(false)}
              className="text-xs font-black uppercase tracking-widest text-emerald-600 hover:text-emerald-700"
            >
              Collapse to top {TOP_N}
            </button>
          ) : (
            <button
              data-testid="mp-section-show-all"
              onClick={() => setShowAll(true)}
              className="text-xs font-black uppercase tracking-widest text-emerald-600 hover:text-emerald-700"
            >
              Show all {rows.length}
            </button>
          )
        )}
        <MarketsRefreshControl
          onRefresh={onRefresh}
          refreshing={refreshing}
          lastUpdatedAt={lastUpdatedAt}
          testid={testid + '-refresh'}
        />
        </div>
      </div>
      {rows.length === 0 ? (
        <div data-testid={testid + '-empty'} className="rounded-xl border border-dashed border-[var(--card-border)] p-6 text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]">
          {emptyLabel}
        </div>
      ) : (
        <div className="w-full">
          <table data-testid="mp-decision-table" className="w-full text-sm table-fixed">
            <thead>
              {/* ADV-MKT-WATCHLIST-UI-POLISH-001: [&>th]:truncate clips long header
                  labels so none bleed into the next column (was overlapping). */}
              <tr className="border-b border-[var(--card-border)] text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] text-left [&>th]:truncate">
                {/* ADV-MKT-SYMBOL-ICON-OVERLAP-001 — Symbol widened 7%→13% so the info
                    icon + ticker + notepad icon fit (ticker readable, not truncated) and
                    never collide with Price. With the sidebar open the table is ~1069px,
                    so 13% ≈ 139px is needed. Reclaimed from Volume/Class/Trend/Bias/Reason.
                    table-fixed ignores min-width, so width is carried by the % alone.
                    Widths still sum to 100%. */}
                {/* ADV-MKT-CAT-COLUMN-LAYOUT-001 — widened Class (5%->8%) for compact
                    leveraged labels + Bias (6%->7%) so the CAT/Class/Bias/Signal region no
                    longer collides; CAT centered at 4%. Reclaimed from $Move/Trend/Reason.
                    Widths still sum to 100%. */}
                <th className="px-2 py-2 w-[13%]">Symbol</th>
                <th className="px-2 py-2 w-[7%]">Price</th>
                <th className="px-2 py-2 w-[7%]">Move %</th>
                <th className="px-2 py-2 w-[7%] hidden md:table-cell">$ Move</th>
                <th className="px-2 py-2 w-[6%] hidden lg:table-cell">Volume</th>
                <th className="px-2 py-2 w-[7%] hidden lg:table-cell">Class</th>
                <th className="px-2 py-2 w-[4%] text-center hidden xl:table-cell" title="Catalyst count">Cat.</th>
                <th className="px-2 py-2 w-[8%]">Bias</th>
                <th className="px-2 py-2 w-[10%]">Signal Use</th>
                <th className="px-2 py-2 w-[9%]">Integrity</th>
                <th className="px-2 py-2 w-[5%] hidden xl:table-cell">Trend</th>
                <th className="px-2 py-2 w-[5%]">Catalyst / Reason</th>
                <th className="px-2 py-2 w-[12%]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {display.map((row, i) => <_Row key={row.symbol + i} row={row} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function MarketPulseDecisionBoard() {
  const [payload, setPayload] = useState<MoversPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 2026-06-07 sitewide-refresh — bumped by adversyn:refresh:market-pulse
  // event so Markets "Run Scan" / MarketPulse refresh also refetches.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancel = false;
    if (reloadToken === 0) setLoading(true); else setRefreshing(true);
    setError(null);
    // ADV-MKT-PREDEPLOY-003A (A1 dedup): share one coalesced /market-pulse/movers
    // request with the heatmap (and run-scan) instead of a separate ~1.4MB pull.
    coalescedGetJson(apiV1Base() + '/market-pulse/movers?view=board', 3500, reloadToken !== 0) // ADV-MKT-PREDEPLOY-003B slim board payload // ADV-MKT-PREDEPLOY-003A: refresh (reloadToken>0) bypasses cache
      .then((r) => {
        if (cancel) return;
        if (!r.ok) {
          setError(`HTTP ${r.status}`);
          if (reloadToken === 0) setPayload(null);
        } else {
          const body = r.json;
          setPayload(body?.data ?? body);
          setLastUpdatedAt(Date.now());
        }
        setLoading(false);
        setRefreshing(false);
      })
      .catch((e) => {
        if (cancel) return;
        setError(String(e?.message || e));
        setLoading(false);
        setRefreshing(false);
      });
    return () => { cancel = true; };
  }, [reloadToken]);

  useEffect(() => {
    const off = subscribeRefresh('market-pulse', () => setReloadToken((t) => t + 1));
    return () => { off(); };
  }, []);

  const { gainers, inverseGainers, losers, shortCandidates, reversalWatch, ignore, summary } = useMemo(() => {
    const raw_gainers = (payload?.top_gainers || []).map(_classifyRow);
    const raw_losers = (payload?.top_losers || []).map(_classifyRow);
    // Buckets are driven by the backend's safe signal_use verdict (via SIGNAL_POLICY),
    // never by move% thresholds. Gainers/Losers keep the full raw lists; the derived
    // sections filter on the policy bucket.
    const inverseGainers = raw_gainers.filter((r) => r.instrumentKey === 'leveraged_inverse');
    const bullishGainers = raw_gainers.filter((r) => r.instrumentKey !== 'leveraged_inverse');
    const all = [...raw_gainers, ...raw_losers];
    return {
      gainers: bullishGainers,
      inverseGainers,
      losers: raw_losers,
      shortCandidates: all.filter((r) => r.bucket === 'short'),
      reversalWatch: all.filter((r) => r.bucket === 'review'),
      ignore: all.filter((r) => r.bucket === 'ignore'),
      // ADV-MKT-BOARD-CLARITY-ACTIONABILITY-001 — board-quality summary over the
      // deduped board universe (gainers ∪ losers), keyed by actionability category.
      summary: summarizeBoard(Array.from(new Map(all.map((r) => [r.symbol, r])).values())),
    };
  }, [payload]);

  const handleRefresh = () => emitRefresh('market-pulse');

  if (loading) {
    return (
      <div data-testid="mp-decision-loading" className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]">
        Loading Market Pulse movers…
      </div>
    );
  }
  if (!payload) {
    return (
      <div data-testid="mp-decision-unavailable" className="bg-[var(--card-bg)] border border-dashed border-[var(--card-border)] rounded-2xl p-8 text-center text-xs font-bold uppercase tracking-widest text-[var(--muted-text)]">
        Market Pulse movers unavailable — try again after the next provider refresh.
      </div>
    );
  }

  return (
   <WatchlistMembershipProvider>
    <div data-testid="mp-decision-board" className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div data-testid="mp-decision-disclaimer" className="text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
            Source: /api/v1/market-pulse/movers · Labels are advisory only. Adversyn never places orders on your behalf.
          </div>
          {payload && <_SessionSnapshotChip payload={payload} />}
          <BoardLegend />
        </div>
        <button
          type="button"
          data-testid="paper-plan-drafts-launcher"
          onClick={() => { try { window.dispatchEvent(new CustomEvent('adversyn:open-paper-drafts')); } catch { /* ignore */ } }}
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border border-[var(--card-border)] text-[var(--muted-text)] hover:text-[var(--app-text)] hover:bg-[var(--input-bg)]"
        >
          <ListChecks size={11} /> Paper Session Drafts
        </button>
      </div>
      {/* ADV-MKT-BOARD-CLARITY-ACTIONABILITY-001 — compact board-quality summary
          (count line, NOT a banner): how many rows are actionable vs held and why. */}
      <BoardSummaryCounts summary={summary} />
      {/* ADV-P0I: compact provider-stack / API-usage visibility tile */}
      <ProviderStackTile />
      <_Section
        testid="mp-section-biggest-gainers"
        title="Biggest Gainers"
        icon={TrendingUp}
        rows={gainers}
        emptyLabel="No gainers in the current window."
        onRefresh={handleRefresh}
        refreshing={refreshing}
        lastUpdatedAt={lastUpdatedAt}
      />
      <_Section
        testid="mp-section-leveraged-inverse"
        title="Leveraged Inverse — reflects underlying weakness"
        icon={AlertOctagon}
        rows={inverseGainers}
        emptyLabel="No leveraged inverse gainers in the current window."
        onRefresh={handleRefresh}
        refreshing={refreshing}
        lastUpdatedAt={lastUpdatedAt}
      />
      <_Section
        testid="mp-section-biggest-losers"
        title="Biggest Losers"
        icon={TrendingDown}
        rows={losers}
        emptyLabel="No losers in the current window."
        onRefresh={handleRefresh}
        refreshing={refreshing}
        lastUpdatedAt={lastUpdatedAt}
      />
      <_Section
        testid="mp-section-short-candidates"
        title="Short Candidates"
        icon={Activity}
        rows={shortCandidates}
        emptyLabel="No short candidates surfaced this window."
        onRefresh={handleRefresh}
        refreshing={refreshing}
        lastUpdatedAt={lastUpdatedAt}
      />
      <_Section
        testid="mp-section-reversal-watch"
        title="Reversal Watch"
        icon={RefreshCw}
        rows={reversalWatch}
        emptyLabel="No reversal-watch candidates this window."
        onRefresh={handleRefresh}
        refreshing={refreshing}
        lastUpdatedAt={lastUpdatedAt}
      />
      <_Section
        testid="mp-section-ignore"
        title="Ignore / Low Quality"
        icon={AlertOctagon}
        rows={ignore}
        emptyLabel="No low-quality movers flagged this window."
        onRefresh={handleRefresh}
        refreshing={refreshing}
        lastUpdatedAt={lastUpdatedAt}
      />
    </div>
   </WatchlistMembershipProvider>
  );
}
